import React, { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/css';
import { useApp } from '../../context/app-context';
import { delay, get, has, invoke, isEmpty, round } from 'lodash';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

export const TooltipContainer = () => {
  const { tooltip, closeTooltip } = useApp();
  const containerRef = useRef(null);
  const message = get(tooltip, 'message');
  const link = get(tooltip, 'link');
  const target = get(tooltip, 'target');
  const pos = get(tooltip, 'pos');
  const clientRects = invoke(target, 'getBoundingClientRect');
  const scrollDiff = get(document, 'documentElement.scrollTop') - get(document, 'documentElement.clientTop');
  const screenWidth = get(document, 'documentElement.clientWidth');
  const screenHeight = get(document, 'documentElement.clientHeight');
  const portalElement = document.getElementById('sns-portal') as HTMLElement;
  const popoverRef = useRef(null);
  const [positionStyle, setPositionStyle] = useState('');
  const [phase, setPhase] = useState(css`
    display: none;
  `);

  const top = has(pos, 'y')
    ? get(pos, 'y', 0)
    : round(get(clientRects, 'top') + get(clientRects, 'height') + scrollDiff + 10);
  const left = has(pos, 'x') ? get(pos, 'left', 0) : round(get(clientRects, 'x') - 10);

  const clickHandler = useCallback((evt) => {
    if (!evt.composedPath().includes(get(containerRef, 'current'))) {
      closeTooltip();
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(message)) {
      delay(() => document.addEventListener('click', clickHandler), 0);
    } else {
      setPositionStyle(css`
        display: none;
      `);
      delay(() => document.removeEventListener('click', clickHandler), 0);
    }
  }, [message]);

  const setStyle = () => {
    const bounds = invoke(popoverRef, 'current.getBoundingClientRect');

    const croppedLeft = get(bounds, 'left') < 0;
    const croppedRight = get(bounds, 'right') > screenWidth;
    const croppedTop = get(bounds, 'top') < 0;
    const croppedBottom = get(bounds, 'bottom') > screenHeight;

    if (phase === 'mount') {
      setPositionStyle(css`
        position: absolute;
        top: ${top}px;
        left: ${left}px;
        opacity: 0;

        @media (max-width: 641px) {
          top: ${top}px;
          left: 1rem;
          right: 1rem;
        }
      `);
      setPhase('layout');
    } else if (phase === 'layout') {
      setPositionStyle(css`
        position: absolute;
        top: ${croppedTop ? '10px' : croppedBottom ? null : `${top}px`};
        bottom: ${croppedBottom ? `${screenHeight - top + 35}px` : null};
        left: ${croppedLeft ? '10px' : croppedRight ? null : `${left}px`};
        right: ${croppedRight ? '20px' : null};
        z-index: 1050;

        @media (max-width: 641px) {
          top: ${top}px;
          bottom: initial;
          left: 1rem;
          right: 1rem;
        }
      `);
    }
  };

  useEffect(() => {
    if (top && left) {
      setPhase('mount');
    }
  }, [top, left]);

  useEffect(() => {
    invoke(window, 'requestAnimationFrame', () => setStyle());
  }, [phase]);

  return ReactDOM.createPortal(
    <div ref={containerRef}>
      {!isEmpty(message) ? (
        <div className={positionStyle} ref={popoverRef}>
          <PopOver content={message} onClose={closeTooltip} link={link} />
        </div>
      ) : null}
    </div>,
    portalElement,
  );
};

const PopOver = (props) => {
  const content = get(props, 'content');
  const onClose = get(props, 'onClose');
  const link = get(props, 'link');
  const className = get(props, 'className');
  const closeBtnRef = useRef(null);
  const [previousFocusedElement, setPreviousFocusedElement] = useState<Element | null>(null);

  const modalStyles = css`
    color: #535250;
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 4px 8px 0 rgba(56, 51, 46, 0.16);
    max-width: 24rem;
    min-width: 24rem;
    z-index: 1050;

    @media (max-width: 641px) {
      left: 0.6rem;
      right: 0.6rem;
      min-width: initial;
    }
  `;

  const closeIconContainer = css`
    display: flex;
    padding: 0.5rem 0;
    justify-content: flex-end;
  `;

  const closeIcon = css`
    cursor: pointer;
    background: 0;
    border: 0;
    padding: 0;
  `;

  const contentContainer = css`
    padding: 0 1rem 0 0;
  `;

  const linkStyle = css`
    color: #7b1a27;
  `;

  useEffect(() => {
    setPreviousFocusedElement(document.activeElement);
    delay(() => invoke(closeBtnRef.current, 'focus', { focusVisible: true }), 50);
  }, []);

  const closePopover = () => {
    delay(() => invoke(previousFocusedElement, 'focus', { focusVisible: true }), 50);
    onClose();
  };

  return (
    <div>
      <div className={`${modalStyles} ${className}`}>
        <div className={closeIconContainer}>
          <button className={closeIcon} aria-label={'Close'} onClick={closePopover} ref={closeBtnRef}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden={true}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.46967 19.5302C4.76256 19.8231 5.23744 19.8231 5.53033 19.5302L19.5302 5.53033C19.8231 5.23744 19.8231 4.76256 19.5302 4.46967C19.2373 4.17678 18.7625 4.17678 18.4696 4.46967L4.46967 18.4696C4.17678 18.7625 4.17678 19.2373 4.46967 19.5302Z"
                fill="#60151E"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M19.5303 19.5303C19.8232 19.2374 19.8232 18.7626 19.5303 18.4697L5.53045 4.46977C5.23755 4.17688 4.76268 4.17688 4.46979 4.46977C4.17689 4.76266 4.17689 5.23754 4.46979 5.53043L18.4697 19.5303C18.7626 19.8232 19.2375 19.8232 19.5303 19.5303Z"
                fill="#60151E"
              />
            </svg>
          </button>
        </div>
        <div className={contentContainer}>
          <p className="body-text-sm">{content}</p>
          {link && (
            <div className={`${linkStyle} links`}>
              <a className="links__action underline link-sm" href={link}>
                More Info
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PopOver.propTypes = {
  content: PropTypes.any.isRequired,
  onClose: PropTypes.func,
  link: PropTypes.string,
  className: PropTypes.string,
};

export default PopOver;
